/* import __COLOCATED_TEMPLATE__ from './prism-slider-input.hbs'; */
import { set, computed } from '@ember/object';
import Component from '@ember/component';
import { assert } from '@ember/debug';
import { htmlSafe } from '@ember/template';
import { COLOR_WHITE, COLOR_GREY_200, COLOR_BLUE_100, COLOR_BLUE_200 } from '@square/ember-prism/utils/colors';
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;

// When the value of the slider input is changed with a keyboard press _and_
// a modifier key, value is increased by `step` multiplied by this value.
//
// For example with step=1 and value=0:
//   RIGHT_ARROW      -> value+=1
//   CTRL+RIGHT_ARROW -> value+=4
const KEY_MODIFIER_MULTIPLIER = 4;
const MINIMUM_AVAILABLE_OFFER_WIDTH = 16;
const MINIMUM_WHITE_SPACE = 2;
const SNAP_POINT_THRESHOLD = 5; // percentage distance to a snap point before the slider snaps to it

function closest(array, goal) {
  return array.reduce(function (prev, curr) {
    return Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev;
  });
}
function assertTrackPartitions(trackPartitions, trackSections) {
  let counter = 0;
  trackPartitions.forEach(partition => {
    counter += partition.size;
    assert('track partition has an off colour', partition.off);
    assert('track partition has an on colour', partition.on);
  });
  assert('correct number of track partitions', counter === trackSections);
}

/**
 * This component implements a custom range input in the form of a slider.
 *
 * @class PrismSliderInput
 */
export default Component.extend({
  classNames: ['prism-slider-input'],
  /**
   * Minimum value of the slider, if min < 0, the part smaller than 0 is
   * unselecteable.
   *
   * @argument min
   * @type {Integer}
   */
  min: 0,
  /**
   * Maximum value of the slider.
   *
   * @argument max
   * @type {Integer}
   */
  max: 100,
  /**
   * Step size of the slider.
   *
   * This controls how much a movement of the slider changes the selected value.
   * This value defaults to `1`.
   *
   * @argument step
   * @type {Number}
   */
  step: 1,
  /**
   * Value of the input.
   *
   * @argument value
   * @type {Integer}
   */
  value: null,
  /**
   * Action called with a value when the slider is updated.
   *
   * @argument onChange
   * @type {Action}
   */
  onChange() {},
  /**
   * @argument tabindex
   * @type {Number}
   */
  tabindex: 0,
  /**
   * Flag for rendering in a market component, so we can call drawTrack once the
   * final size of the canvas element is set
   *
   * @argument inMarketComponent
   * @type {Boolean}
   */
  inMarketComponent: false,
  /**
   * Describes rules for how to partition the slider track into differently
   * coloured sections.
   *
   * Each object in the array must have the properties `size`, `off`, and `on`.
   * `off` and `on` are HEX colour values for the track. `size` is the number of
   * track sections that the object applies to.
   *
   * The sum of all `size` values must be exactly equal to the number of track
   * sections.
   *
   * @argument trackPartitions
   * @type {Array<Object>}
   */
  trackPartitions: computed('_trackPartitions', '_trackSections', {
    get() {
      if (this._trackPartitions) {
        return this._trackPartitions;
      }
      // Apply a default track partition if none exists.
      return [{
        size: this._trackSections,
        off: COLOR_GREY_200,
        on: COLOR_BLUE_200
      }];
    },
    set(key, value) {
      set(this, '_trackPartitions', value);
      return this._trackPartitions;
    }
  }),
  /**
   * Hex value for thumb's active color. Defaults to $color-blue-100.
   * @argument thumbActiveColor
   * @type {String}
   */
  thumbActiveColor: COLOR_BLUE_100,
  /**
   * This function is used to convert the value of the input into something
   * human readable for accessiblity.
   *
   * By default this just returns value.
   *
   * @argument getHumanReadableValue
   * @type {Function}
   */
  getHumanReadableValue(value) {
    return value;
  },
  thumbIsActive: false,
  accessibleMin: computed('min', 'getHumanReadableValue', function () {
    return this.getHumanReadableValue(this.min);
  }),
  accessibleMax: computed('max', 'getHumanReadableValue', function () {
    return this.getHumanReadableValue(this.max);
  }),
  accessibleValue: computed('value', 'getHumanReadableValue', function () {
    return this.getHumanReadableValue(this.value);
  }),
  // Memoize a reversed partitions array.
  _trackPartitionsReversed: computed('trackPartitions', function () {
    return [...this.trackPartitions].reverse();
  }),
  // Calculate total number of track sections.
  _trackSections: computed('min', 'max', function () {
    return this.max - this.min;
  }),
  thumbStyle: computed('currentThumbPercentage', function () {
    const percent = this.currentThumbPercentage || 0;
    return htmlSafe(`left: ${percent * 100}%;`);
  }),
  thumbIsSnappedToSnapPoint: computed('snapPoints', 'value', function () {
    if (!Array.isArray(this.snapPoints)) {
      return false;
    }
    return this.snapPoints.includes(this.value);
  }),
  currentThumbPercentage: computed('value', 'min', 'max', function () {
    const min = Number.parseInt(this.min, 10);
    const max = Number.parseInt(this.max, 10);
    return (this.value - min) / (max - min);
  }),
  snapPointStyles: computed('max', 'min', 'snapPoints', function () {
    const max = Number.parseInt(this.max, 10);
    const min = Number.parseInt(this.min, 10);
    return this.snapPoints.map(point => htmlSafe(`left: ${(point - min) / (max - min) * 100}%;`));
  }),
  init(...args) {
    this._super(...args);
    this.set('value', this.minMaxValidator(this.value));
    assertTrackPartitions(this.trackPartitions, this._trackSections);
  },
  positionToPercent(xPosition) {
    return Math.max(0, Math.min(1, (xPosition - this.sliderDimensions().left) / this.sliderDimensions().width));
  },
  percentToValue(xPosition) {
    const min = Number.parseInt(this.min, 10);
    const max = Number.parseInt(this.max, 10);
    return min + xPosition * (max - min);
  },
  minMaxValidator(value) {
    const min = Math.max(Number.parseInt(this.min, 10), 0);
    const max = Number.parseInt(this.max, 10);
    return Math.max(min, Math.min(max, value));
  },
  stepValidator(value) {
    const step = Number.parseInt(this.step, 10);
    const formattedValue = Math.round(value / step) * step;
    return formattedValue;
  },
  sliderDimensions() {
    return this.element.getBoundingClientRect();
  },
  handleThumbChange(event) {
    this._onChange(event.value);
  },
  setValueFromEvent(event) {
    const exactVal = this.percentToValue(this.positionToPercent(event.clientX || event.touches[0].clientX));
    let relativeDistanceToSnapPoint;
    let closestSnapPoint;
    if (Array.isArray(this.snapPoints)) {
      closestSnapPoint = closest(this.snapPoints, exactVal);
      relativeDistanceToSnapPoint = Math.abs(exactVal - closestSnapPoint) / this.max * 100;
    }
    if (relativeDistanceToSnapPoint !== undefined && relativeDistanceToSnapPoint <= SNAP_POINT_THRESHOLD && exactVal !== this.min && exactVal < this.max - 1) {
      this._onChange(closestSnapPoint);
    } else {
      const closestVal = this.minMaxValidator(this.stepValidator(exactVal));
      this._onChange(closestVal);
    }
  },
  _onChange(value) {
    this.set('value', value);
    this.onChange(this.value);
    this.drawTrack();
  },
  // When movement begins (by click or touch starting) this function attaches
  // handlers which fire repeatedly as the mouse or touch moves then when it
  // ends it cleans up the movement handler.
  _moveStart(moveEvent, endEvent, event) {
    event.preventDefault();
    this.element.focus();
    this.set('thumbIsActive', true);
    const moveHandler = this.setValueFromEvent.bind(this);
    document.addEventListener(moveEvent, moveHandler, {
      passive: false
    });
    document.addEventListener(endEvent, () => {
      this.set('thumbIsActive', false);
      document.removeEventListener(moveEvent, moveHandler);
    },
    // `once` causes this event listener to remove itself after firing
    {
      once: true
    });
  },
  click(event) {
    this.setValueFromEvent(event);
  },
  focusIn() {
    this.set('thumbIsActive', true);
  },
  focusOut() {
    this.set('thumbIsActive', false);
  },
  /**
   *  This function implements the track drawing algorithm.
   *    We start with a `currentFillCount=trackSections` and iterate through the
   * `trackPartitions` (reversed above).
   *
   * 1. Calculate a fillWidth value (decreasing with currentFillCount)
   * 2. Draw the off color from left to fillWidth
   * 3. Draw the on color from left to the minimum of fillWidth or the
   *    thumb position.
   * 4. Draw a white notch if not on the first partition.
   *
   * For example given the following values:
   *   min=0
   *   max=9
   *   value=4
   *   trackPartitions=[{size: 3, off: c, on: c'}, {4, b, b'}, {2, a, a'}]
   *
   * We start with:
   *   0                            9
   *   |  |  |  |  [|]  |  |  |  |  |
   *                ^
   *                thumb
   *
   * currentFillCount=9
   *
   * And draw the track:
   *   fillWidth = W * 9 / 10
   *   |a |a |a |a [|]a |a |a |a |a |
   *   |a'|a'|a'|a'[|]a |a |a |a |a |
   *                          N
   *   currentFillCount=7
   *
   *   fillWidth = W * 7 / 10
   *   |b |b |b |b [|]b |b |b |a |a |
   *   |b'|b'|b'|b'[|]b |b |b |a |a |
   *            N
   *   currentFillCount=3
   *
   *   fillWidth = W * 3 / 10
   *   |c |c |c |b'[|]b |b |b |a |a |
   *   |c'|c'|c'|b'[|]b |b |b |a |a |
   *   currentFillCount=0
   *
   * @function drawTrack
   */
  drawTrack() {
    const partitions = this._trackPartitionsReversed;
    const trackSections = this._trackSections;
    const canvas = this.element.querySelector('canvas');
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const currentPercent = Number.parseFloat(this.currentThumbPercentage.toFixed(2));
    canvas.setAttribute('width', canvas.clientWidth);
    canvas.setAttribute('height', canvas.clientHeight);
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const currentPosition = Math.round(currentPercent * canvasWidth);

    // Initial values
    let fillWidth;
    let currentFillCount = trackSections;
    partitions.forEach(partition => {
      fillWidth = Math.round(canvasWidth * currentFillCount / trackSections);

      // Edgecase where we are doing the last fill but there is no width left.
      if (currentFillCount === 1 && partition.size === 1) {
        ctx.fillStyle = partition.off;
        ctx.fillRect(0, 0, MINIMUM_AVAILABLE_OFFER_WIDTH, canvasHeight);
        ctx.fillStyle = partition.on;
        ctx.fillRect(0, 0, Math.min(currentPosition, MINIMUM_AVAILABLE_OFFER_WIDTH), canvasHeight);

        // Draw a notch
        ctx.fillStyle = COLOR_WHITE;
        ctx.fillRect(MINIMUM_AVAILABLE_OFFER_WIDTH, 0, MINIMUM_WHITE_SPACE, canvasHeight);
      } else if (fillWidth > 0) {
        ctx.fillStyle = partition.off;
        ctx.fillRect(0, 0, fillWidth, canvasHeight);
        ctx.fillStyle = partition.on;
        ctx.fillRect(0, 0, Math.min(currentPosition, fillWidth), canvasHeight);

        // Draw a notch if this is not the first section being drawn.
        if (currentFillCount !== trackSections) {
          ctx.fillStyle = COLOR_WHITE;
          ctx.fillRect(fillWidth, 0, MINIMUM_WHITE_SPACE, canvasHeight);
        }
      }
      currentFillCount -= partition.size;
    });
  },
  keyDown(event) {
    let changeAmount;
    if (event.keyCode === LEFT_ARROW) {
      changeAmount = this.step * -1;
    } else if (event.keyCode === RIGHT_ARROW) {
      changeAmount = this.step;
    }
    if (changeAmount) {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        changeAmount *= KEY_MODIFIER_MULTIPLIER;
      }
      const closestVal = this.minMaxValidator(this.value + changeAmount);
      this.set('value', closestVal);
      this.onChange(this.value);
      this.drawTrack();
      event.preventDefault();
      event.stopPropagation();
    }
  },
  didInsertElement(...args) {
    this._super(...args);
    this.element.addEventListener('touchstart', event => {
      this._moveStart('touchmove', 'touchend', event);
    });
    this.element.addEventListener('mousedown', event => {
      this._moveStart('mousemove', 'mouseup', event);
    });
  },
  didRender(...args) {
    this._super(...args);
    this.drawTrack();
    if (this.inMarketComponent) {
      const inputElem = document.querySelector('.prism-slider-input__thumb-container');
      const ro = new ResizeObserver(() => {
        if (this.isDestroyed || this.isDestroying) {
          ro.disconnect();
          return;
        }
        this.drawTrack();
        if (inputElem.clientWidth > 0) {
          // without the clientWidth check, the observer sometimes disconnects
          // when the element has rendered and never draws the background properly.
          ro.disconnect();
        }
      });
      ro.observe(inputElem);
    }
  }
});